/**
 * Serviço de análise de transcrições
 */

import {
  Transcription,
  TranscriptionMetrics,
  SpeakerStat,
  SearchResult,
  LegalTerm,
  TermOccurrence
} from '../types/transcription'
import type { CustomTermConfig } from '../types/settings'

// Tipo interno com regex compilada
interface CompiledTerm {
  term: string
  pattern: RegExp
  category: string
  description: string
}

// Converte CustomTermConfig (pattern string) para CompiledTerm (pattern RegExp)
function compileTerm(config: CustomTermConfig): CompiledTerm {
  return {
    term: config.term,
    pattern: new RegExp(config.pattern, 'gi'),
    category: config.category,
    description: config.description
  }
}

/**
 * Calcula métricas da transcrição
 */
export function calculateMetrics(transcription: Transcription): TranscriptionMetrics {
  const { transcript, duration } = transcription
  const totalDuration = parseFloat(duration) * 60 // Converter minutos para segundos

  // Contagem de palavras
  const totalWords = transcript.reduce((acc, seg) => {
    return acc + seg.text.split(/\s+/).filter(w => w.length > 0).length
  }, 0)

  // Estatísticas por speaker
  const speakerMap = new Map<string, { segments: number; duration: number; words: number }>()

  for (const segment of transcript) {
    const speaker = segment.speaker || 'Não identificado'
    const segmentDuration = parseFloat(segment.end_time) - parseFloat(segment.start_time)
    const wordCount = segment.text.split(/\s+/).filter(w => w.length > 0).length

    const current = speakerMap.get(speaker) || { segments: 0, duration: 0, words: 0 }
    speakerMap.set(speaker, {
      segments: current.segments + 1,
      duration: current.duration + segmentDuration,
      words: current.words + wordCount
    })
  }

  const speakerStats: SpeakerStat[] = Array.from(speakerMap.entries()).map(([speaker, stats]) => ({
    speaker,
    segmentCount: stats.segments,
    totalDuration: stats.duration,
    wordCount: stats.words,
    percentage: totalDuration > 0 ? (stats.duration / totalDuration) * 100 : 0
  })).sort((a, b) => b.totalDuration - a.totalDuration)

  const totalSegmentDuration = transcript.reduce((acc, seg) => {
    return acc + (parseFloat(seg.end_time) - parseFloat(seg.start_time))
  }, 0)

  return {
    totalDuration,
    totalWords,
    totalSegments: transcript.length,
    averageSegmentDuration: transcript.length > 0 ? totalSegmentDuration / transcript.length : 0,
    averageWordsPerSegment: transcript.length > 0 ? totalWords / transcript.length : 0,
    speakerCount: speakerMap.size,
    speakerStats,
    wordsPerMinute: totalDuration > 0 ? (totalWords / totalDuration) * 60 : 0
  }
}

/**
 * Busca texto na transcrição
 */
export function searchTranscript(transcription: Transcription, query: string): SearchResult[] {
  if (!query.trim()) return []

  const results: SearchResult[] = []
  const lowerQuery = query.toLowerCase()

  transcription.transcript.forEach((segment, index) => {
    const lowerText = segment.text.toLowerCase()
    let searchIndex = 0

    while (true) {
      const matchStart = lowerText.indexOf(lowerQuery, searchIndex)
      if (matchStart === -1) break

      results.push({
        segmentIndex: index,
        segment,
        matchedText: segment.text.substring(matchStart, matchStart + query.length),
        matchStart,
        matchEnd: matchStart + query.length
      })

      searchIndex = matchStart + 1
    }
  })

  return results
}

/**
 * Encontra termos customizados na transcrição com suas ocorrências
 * @param transcription Transcrição a ser analisada
 * @param customTerms Termos customizados do usuário (mesclados)
 */
export function findLegalTermsInTranscript(
  transcription: Transcription,
  customTerms?: CustomTermConfig[]
): LegalTerm[] {
  if (!customTerms?.length) return []
  
  const termsToUse: CompiledTerm[] = customTerms.filter(t => t.enabled !== false).map(compileTerm)
  
  const termMap = new Map<string, { term: CompiledTerm; occurrences: TermOccurrence[] }>()

  transcription.transcript.forEach((segment, index) => {
    for (const termDef of termsToUse) {
      const pattern = new RegExp(termDef.pattern.source, termDef.pattern.flags)
      let match: RegExpExecArray | null

      while ((match = pattern.exec(segment.text)) !== null) {
        const key = termDef.term.toLowerCase()
        const existing = termMap.get(key) || { term: termDef, occurrences: [] }

        existing.occurrences.push({
          segmentIndex: index,
          startTime: segment.start_time,
          text: segment.text
        })

        termMap.set(key, existing)
      }
    }
  })

  return Array.from(termMap.values())
    .map(({ term, occurrences }) => ({
      term: term.term,
      category: term.category,
      description: term.description,
      occurrences
    }))
    .sort((a, b) => b.occurrences.length - a.occurrences.length)
}

/**
 * Formata timestamp para exibição (mm:ss)
 */
export function formatTimestamp(seconds: string | number): string {
  const secs = typeof seconds === 'string' ? parseFloat(seconds) : seconds
  const mins = Math.floor(secs / 60)
  const remainingSecs = Math.floor(secs % 60)
  return `${mins.toString().padStart(2, '0')}:${remainingSecs.toString().padStart(2, '0')}`
}

/**
 * Formata duração para exibição humana
 */
export function formatDuration(minutes: number): string {
  if (minutes < 1) {
    return `${Math.round(minutes * 60)} segundos`
  }
  if (minutes < 60) {
    const mins = Math.floor(minutes)
    const secs = Math.round((minutes - mins) * 60)
    return secs > 0 ? `${mins}min ${secs}s` : `${mins} minutos`
  }
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  return mins > 0 ? `${hours}h ${mins}min` : `${hours} hora${hours > 1 ? 's' : ''}`
}

/**
 * Destaca termos customizados no texto
 * @param text Texto a ser analisado
 * @param customTerms Termos customizados do usuário (mesclados)
 */
export function highlightLegalTerms(
  text: string,
  customTerms?: CustomTermConfig[]
): { text: string; isHighlight: boolean; term?: CompiledTerm }[] {
  const parts: { text: string; isHighlight: boolean; term?: CompiledTerm }[] = []

  if (!customTerms?.length) return [{ text, isHighlight: false }]

  const termsToUse: CompiledTerm[] = customTerms.filter(t => t.enabled !== false).map(compileTerm)

  // Cria um array de todas as matches com suas posições
  const allMatches: { start: number; end: number; term: CompiledTerm }[] = []

  for (const termDef of termsToUse) {
    const pattern = new RegExp(termDef.pattern.source, termDef.pattern.flags)
    let match: RegExpExecArray | null

    while ((match = pattern.exec(text)) !== null) {
      allMatches.push({
        start: match.index,
        end: match.index + match[0].length,
        term: termDef
      })
    }
  }

  // Ordena por posição
  allMatches.sort((a, b) => a.start - b.start)

  // Remove overlaps (mantém a primeira match)
  const filteredMatches = allMatches.filter((match, index) => {
    if (index === 0) return true
    const prev = allMatches[index - 1]
    return match.start >= prev.end
  })

  // Constrói o array de partes
  let lastEnd = 0
  for (const match of filteredMatches) {
    if (match.start > lastEnd) {
      parts.push({ text: text.substring(lastEnd, match.start), isHighlight: false })
    }
    parts.push({
      text: text.substring(match.start, match.end),
      isHighlight: true,
      term: match.term
    })
    lastEnd = match.end
  }

  if (lastEnd < text.length) {
    parts.push({ text: text.substring(lastEnd), isHighlight: false })
  }

  return parts.length > 0 ? parts : [{ text, isHighlight: false }]
}

// ===== TIPOS PARA DADOS EXTRAÍDOS =====

export interface ExtractedDataOccurrence {
  value: string
  segmentIndex: number
  timestamp: string
  speaker: string
  context: string
}

export interface ExtractedDataWithContext {
  value: string
  occurrences: ExtractedDataOccurrence[]
  normalized?: string | number
}

// ===== FUNÇÕES DE EXTRAÇÃO COM CONTEXTO =====

/**
 * Função auxiliar para extrair dados com contexto completo da transcrição
 */
function extractWithContext(
  transcription: Transcription,
  pattern: RegExp,
  normalizer?: (value: string) => string
): ExtractedDataWithContext[] {
  const resultsMap = new Map<string, ExtractedDataOccurrence[]>()

  transcription.transcript.forEach((segment, index) => {
    const regex = new RegExp(pattern.source, pattern.flags)
    let match: RegExpExecArray | null

    while ((match = regex.exec(segment.text)) !== null) {
      const value = match[0].trim()
      const normalized = normalizer ? normalizer(value) : value

      const occurrence: ExtractedDataOccurrence = {
        value,
        segmentIndex: index,
        timestamp: segment.start_time,
        speaker: segment.speaker || 'Não identificado',
        context: segment.text
      }

      const existing = resultsMap.get(normalized) || []
      existing.push(occurrence)
      resultsMap.set(normalized, existing)
    }
  })

  return Array.from(resultsMap.entries()).map(([normalized, occurrences]) => ({
    value: occurrences[0].value, // Usa a primeira aparição como valor principal
    occurrences,
    normalized
  }))
}

/**
 * Extrai números de processo do texto com contexto
 */
export function extractProcessNumbers(transcription: Transcription): ExtractedDataWithContext[] {
  // Padrão CNJ: NNNNNNN-DD.AAAA.J.TR.OOOO
  const cnjPattern = /\d{7}-\d{2}\.\d{4}\.\d\.\d{2}\.\d{4}/g
  return extractWithContext(transcription, cnjPattern)
}

/**
 * Extrai valores monetários com contexto
 */
export function extractMonetaryValues(transcription: Transcription): ExtractedDataWithContext[] {
  const pattern = /R\$\s*[\d.,]+/gi
  
  const normalizer = (value: string): string => {
    // Remove R$ e espaços, normaliza separadores
    return value.replace(/R\$/gi, '').replace(/\s/g, '').trim()
  }

  return extractWithContext(transcription, pattern, normalizer)
}

/**
 * Extrai menções a datas com contexto
 */
export function extractDates(transcription: Transcription): ExtractedDataWithContext[] {
  const patterns = [
    /\d{1,2}\s+de\s+(?:janeiro|fevereiro|março|abril|maio|junho|julho|agosto|setembro|outubro|novembro|dezembro)\s+(?:de\s+)?\d{4}/gi,
    /\d{1,2}\/\d{1,2}\/\d{2,4}/g,
    /\d{1,2}-\d{1,2}-\d{2,4}/g
  ]

  const allResults: ExtractedDataWithContext[] = []
  
  patterns.forEach(pattern => {
    const results = extractWithContext(transcription, pattern)
    allResults.push(...results)
  })

  return allResults
}

/**
 * Extrai CPFs com contexto
 */
export function extractCPFs(transcription: Transcription): ExtractedDataWithContext[] {
  const pattern = /\d{3}\.\d{3}\.\d{3}-\d{2}/g
  return extractWithContext(transcription, pattern)
}

/**
 * Extrai RGs com contexto
 */
export function extractRGs(transcription: Transcription): ExtractedDataWithContext[] {
  const pattern = /\d{1,2}\.\d{3}\.\d{3}-\d{1,2}/g
  return extractWithContext(transcription, pattern)
}

/**
 * Extrai CNHs com contexto
 */
export function extractCNHs(transcription: Transcription): ExtractedDataWithContext[] {
  const pattern = /\d{11}/g // CNH tem 11 dígitos
  const results = extractWithContext(transcription, pattern)
  
  // Filtrar para remover possíveis falsos positivos (ex: telefones)
  // Aceitar apenas se estiver próximo de palavras como "CNH", "carteira", "habilitação"
  return results.filter(result => 
    result.occurrences.some(occ => 
      /\b(?:cnh|carteira|habilita[çc][ãa]o)\b/i.test(occ.context)
    )
  )
}

/**
 * Extrai artigos do Código Penal com contexto
 */
export function extractLegalArticles(transcription: Transcription): ExtractedDataWithContext[] {
  const pattern = /(?:art\.?|artigo)\s*\d+(?:[º°])?(?:\s*,?\s*(?:§|par[áa]grafo|caput)\s*\d+(?:[º°])?)?(?:\s+(?:do\s+)?(?:CP|C[óo]digo\s+Penal|CPP|C[óo]digo\s+de\s+Processo\s+Penal))?/gi
  
  const normalizer = (value: string): string => {
    // Normaliza para formato padrão: "Art. XXX CP"
    const match = value.match(/\d+/)
    return match ? `Art. ${match[0]}` : value
  }

  return extractWithContext(transcription, pattern, normalizer)
}

/**
 * Extrai leis específicas com contexto
 */
export function extractLaws(transcription: Transcription): ExtractedDataWithContext[] {
  const pattern = /Lei\s+(?:n[º°]\.?\s*)?\d+\.?\d*(?:\/\d{2,4})?/gi
  return extractWithContext(transcription, pattern)
}

/**
 * Extrai súmulas com contexto
 */
export function extractSumulas(transcription: Transcription): ExtractedDataWithContext[] {
  const pattern = /S[úu]mula\s+(?:Vinculante\s+)?(?:n[º°]\.?\s*)?\d+/gi
  return extractWithContext(transcription, pattern)
}
